import { RACSearchDropdown } from '@rentacenter/racstrap';
import { get } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import {
  ACIMA,
  ClassificationsInputPlaceholder,
  emptyField,
  PoFormField,
} from '../../constants/constants';
import { ClassificationType } from '../../domain/PurchaseOrder/ClassificationType';
import { PoStatus } from '../../domain/PurchaseOrder/PoStatus';
import { SelectedFieldOption } from '../../types/types';
import { filterById, populateSelectByChild } from '../../utils/utils';
import {
  useClassificationsDispatch,
  useClassificationsState,
} from './context/Provider';
import { getClassificationFieldName } from './context/fieldNames';
import { ClassificationsComponentProps } from './context/types';

export const departmentTestId = 'departmentComponent';

// eslint-disable-next-line
export const Department = (props: ClassificationsComponentProps) => {
  const {
    name,
    label,
    purchaseOrder,
    qrScannerOutput,
    isCoupaOrPcrd,
    showErrorMessage,
  } = props;
  const { errors, setValue, getValues } = useFormContext();
  const dispatch = useClassificationsDispatch();
  const { initialDepartments, departments, startWithTypes } =
    useClassificationsState();
  const [itemValue, setItemValue] = useState(emptyField);
  const [isViewMode, setIsViewMode] = useState(false);
  const getFormFieldName = useCallback(
    (fieldName: PoFormField) => getClassificationFieldName(name, fieldName),
    [name]
  );

  useEffect(
    // eslint-disable-next-line
    () => {
      const poDepartment = purchaseOrder && purchaseOrder.department;
      const poStartClassificationsSelect =
        purchaseOrder && purchaseOrder.startClassificationsSelect;

      const optionDepartments =
        departments && departments.length ? departments : initialDepartments;
      const selectedDepartment: {
        name: string;
        id: string;
        subDepartments?: [];
      } | null = poDepartment && filterById(optionDepartments, poDepartment)[0];

      if (startWithTypes) {
        if (departments && departments.length === 1) {
          setItemValue({
            value: departments[0].id,
            label: departments[0].name,
          });
          dispatch({
            type: ClassificationType.SET_SINGLE_SUBDEPARTMENT,
            payload: departments[0].subDepartments,
          });
        } else {
          setItemValue(emptyField);
          dispatch({
            type: ClassificationType.CLEAR_SUBDEPARTMENTS,
          });
        }
      }

      if (initialDepartments && poDepartment && selectedDepartment) {
        if (poStartClassificationsSelect === 'department' && !startWithTypes) {
          dispatch({
            type: ClassificationType.SET_DEPARTMENT,
            payload: selectedDepartment,
          });
        }

        if (startWithTypes) {
          if (departments && departments.length === 0) {
            dispatch({
              type: ClassificationType.SET_DEPARTMENTS,
              payload: [selectedDepartment],
            });
          }

          dispatch({
            type: ClassificationType.SET_SUBDEPARTMENTS_FROM_DEPARTMENT,
            payload: selectedDepartment.subDepartments,
          });
        }
      }

      if (qrScannerOutput && optionDepartments?.length > 0) {
        const selectedDepartment = optionDepartments.find(
          (o) => o.id == qrScannerOutput.departmentNumber
        );
        setItemValue({
          value: selectedDepartment?.id
            ? selectedDepartment.id
            : qrScannerOutput?.departmentNumber?.toString() || '',
          label: selectedDepartment?.name
            ? selectedDepartment?.name
            : qrScannerOutput?.department
            ? qrScannerOutput?.department
            : '',
        });
        setValue(
          getFormFieldName(PoFormField.StartClassificationsSelect),
          PoFormField.Department
        );
        setValue(
          getFormFieldName(PoFormField.Department),
          selectedDepartment?.id
            ? selectedDepartment?.id
            : qrScannerOutput?.departmentNumber?.toString(),
          {
            shouldValidate: true,
            shouldDirty: true,
          }
        );
        setIsViewMode(
          qrScannerOutput?.isScannedCategoryKept
            ? qrScannerOutput?.isScannedCategoryKept
            : false
        );
        // }
        if (!qrScannerOutput.departmentNumber) {
          setItemValue(emptyField);
          dispatch({
            type: ClassificationType.CLEAR_SUBDEPARTMENTS,
          });
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [departments, startWithTypes, initialDepartments, dispatch, qrScannerOutput]
  );

  useEffect(
    // eslint-disable-next-line
    () => {
      const poDepartment = purchaseOrder && purchaseOrder.department;

      const optionDepartments =
        departments && departments.length ? departments : initialDepartments;
      const selectedDepartment: {
        name: string;
        id: string;
        subDepartments?: [];
      } | null = poDepartment && filterById(optionDepartments, poDepartment)[0];

      poDepartment &&
        setItemValue({
          value: poDepartment || '',
          label:
            (selectedDepartment && selectedDepartment.name) ||
            ClassificationsInputPlaceholder,
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [departments, initialDepartments]
  );

  const formStartClassificationsSelect = getValues(
    getFormFieldName(PoFormField.StartClassificationsSelect)
  );

  const handleChange = useCallback(
    // eslint-disable-next-line
    (selected: SelectedFieldOption) => {
      const { value, label, subDepartments } = selected;
      setItemValue({ value, label });
      setValue(name, value, {
        shouldValidate: true,
        shouldDirty: true,
      });
      formStartClassificationsSelect !== PoFormField.ItemSubType &&
        setValue(
          getFormFieldName(PoFormField.StartClassificationsSelect),
          PoFormField.Department
        );
      if (startWithTypes) {
        dispatch({
          type: ClassificationType.SET_SUBDEPARTMENTS_FROM_DEPARTMENT,
          payload: subDepartments,
        });
      }

      if (startWithTypes && purchaseOrder && subDepartments) {
        let brackets = subDepartments[0].brackets;

        if (purchaseOrder && purchaseOrder.department !== value) {
          purchaseOrder.subDepartment = '';
          purchaseOrder.bracket = '';
          purchaseOrder.itemType = '';
          purchaseOrder.itemSubType = '';
        }

        if (subDepartments.length === 1) {
          setValue(
            getFormFieldName(PoFormField.SubDepartment),
            subDepartments[0].id,
            {
              shouldValidate: true,
              shouldDirty: true,
            }
          );
        } else {
          brackets = [];

          setValue(getFormFieldName(PoFormField.SubDepartment), '', {
            shouldValidate: false,
            shouldDirty: true,
          });
        }

        if (brackets && brackets.length === 1) {
          setValue(getFormFieldName(PoFormField.Bracket), brackets[0].id, {
            shouldValidate: true,
            shouldDirty: true,
          });
        } else {
          brackets = [];

          setValue(getFormFieldName(PoFormField.Bracket), '', {
            shouldValidate: false,
            shouldDirty: true,
          });
        }
      }

      if (!startWithTypes && subDepartments) {
        dispatch({
          type: ClassificationType.SET_DEPARTMENT,
          payload: selected,
        });

        let brackets = subDepartments?.[0]?.brackets;
        let types = brackets?.[0]?.types;
        let subTypes = types?.[0]?.subTypes;

        if (subDepartments?.length === 1) {
          setValue(
            getFormFieldName(PoFormField.SubDepartment),
            subDepartments[0].id,
            {
              shouldValidate: true,
              shouldDirty: true,
            }
          );
        } else {
          brackets = [];
          types = [];
          subTypes = [];
          setValue(getFormFieldName(PoFormField.SubDepartment), '', {
            shouldValidate: false,
            shouldDirty: true,
          });
        }

        if (brackets && brackets?.length === 1) {
          setValue(getFormFieldName(PoFormField.Bracket), brackets[0].id, {
            shouldValidate: true,
            shouldDirty: true,
          });
        } else {
          brackets = [];
          types = [];
          subTypes = [];
          setValue(getFormFieldName(PoFormField.Bracket), '', {
            shouldValidate: false,
            shouldDirty: true,
          });
        }

        if (types && types?.length === 1) {
          setValue(getFormFieldName(PoFormField.ItemType), types[0].id, {
            shouldValidate: true,
            shouldDirty: true,
          });
        } else {
          brackets = [];
          types = [];
          subTypes = [];
          setValue(getFormFieldName(PoFormField.ItemType), '', {
            shouldValidate: false,
            shouldDirty: true,
          });
        }

        if (subTypes && subTypes?.length === 1) {
          setValue(getFormFieldName(PoFormField.ItemSubType), subTypes[0].id, {
            shouldValidate: true,
            shouldDirty: true,
          });
        } else {
          brackets = [];
          types = [];
          subTypes = [];
          setValue(getFormFieldName(PoFormField.ItemSubType), '', {
            shouldValidate: false,
            shouldDirty: true,
          });
        }
      }
    },
    [
      name,
      purchaseOrder,
      startWithTypes,
      dispatch,
      formStartClassificationsSelect,
      getFormFieldName,
      setValue,
    ]
  );

  const removeXAcimaParents = useCallback((departments: any[]) => {
    return departments
      .map((dep) => {
        if (!dep.subDepartments) return dep;
        // Filter out sub-departments containing "ACIMA" (case-insensitive)
        const filteredSubDepts = dep.subDepartments.filter(
          (subDept: { name: string }) =>
            !subDept.name.toUpperCase().includes(ACIMA)
        );

        // If all sub-departments were removed, remove the entire department
        if (filteredSubDepts.length === 0) {
          return null;
        }

        return {
          ...dep,
          subDepartments: filteredSubDepts,
        };
      })
      .filter((d) => d !== null);
  }, []);

  const departmentOptions = useCallback(() => {
    const initialFilteredDepartments =
      isCoupaOrPcrd && initialDepartments
        ? removeXAcimaParents(initialDepartments)
        : initialDepartments;
    return startWithTypes
      ? departments && populateSelectByChild(departments, 'subDepartments')
      : populateSelectByChild(initialFilteredDepartments, 'subDepartments');
  }, [
    departments,
    initialDepartments,
    startWithTypes,
    isCoupaOrPcrd,
    removeXAcimaParents,
  ]);

  useEffect(
    // eslint-disable-next-line
    () => {
      setIsViewMode(
        purchaseOrder
          ? purchaseOrder?.poStatus === PoStatus.SUBMITTED ||
              purchaseOrder.poStatus === PoStatus.OPEN_CANCELLED
          : false
      );
    },
    [purchaseOrder]
  );

  return (
    <Controller
      name={name}
      rules={{ required: true }}
      render={({ name }) => (
        <RACSearchDropdown
          name={name}
          inputLabel={label}
          options={departmentOptions() || []}
          OnChange={handleChange}
          required
          disabled={!!isViewMode}
          value={itemValue}
          errormessage={
            showErrorMessage && get(errors, name) ? `${label} is required` : ''
          }
        />
      )}
    />
  );
};
